import { toast } from 'react-toastify';
import notificationDebugger from './notificationDebugger';

class NotificationManager {
  constructor() {
    this.recentNotifications = new Map();
    this.sessionNotifications = new Set(); // Para notificações que devem aparecer apenas uma vez por sessão
    this.cleanupInterval = 30000; // 30 segundos
    this.activeGuicheToast = null; // Referência para a notificação ativa de guichê
    
    // Carregar notificações de sessão salvas do localStorage
    this.loadSessionNotificationsFromStorage();
    
    // Limpar notificações antigas periodicamente
    setInterval(() => {
      this.cleanupOldNotifications();
    }, this.cleanupInterval);
  }

  // Gerar ID único para notificação baseado no tipo e conteúdo
  generateNotificationId(type, content) {
    return `${type}-${content}`;
  }

  // Verificar se notificação já foi exibida recentemente
  isRecentNotification(id) {
    const now = Date.now();
    const lastShown = this.recentNotifications.get(id);
    
    // Considerar "recente" se foi exibida nos últimos 5 segundos
    return lastShown && (now - lastShown) < 5000;
  }

  // Registrar que uma notificação foi exibida
  recordNotification(id) {
    this.recentNotifications.set(id, Date.now());
  }

  // Limpar notificações antigas
  cleanupOldNotifications() {
    const now = Date.now();
    const cutoff = now - 30000; // 30 segundos atrás
    
    for (const [id, timestamp] of this.recentNotifications.entries()) {
      if (timestamp < cutoff) {
        this.recentNotifications.delete(id);
      }
    }
  }

  // Método principal para exibir notificação com deduplicação
  showNotification(type, message, options = {}) {
    const id = this.generateNotificationId(type, message);
    
    // Verificar se já foi exibida recentemente
    if (this.isRecentNotification(id)) {
      console.log(`🚫 Notificação duplicada bloqueada: ${message}`);
      return null;
    }

    // Registrar e exibir notificação
    this.recordNotification(id);
    
    // Rastrear para debugging
    notificationDebugger.trackNotification(type, message, 'NotificationManager');
    
    switch (type) {
      case 'info':
        return toast.info(message, options);
      case 'success':
        return toast.success(message, options);
      case 'warning':
        return toast.warning(message, options);
      case 'error':
        return toast.error(message, options);
      default:
        return toast(message, options);
    }
  }

  // Métodos específicos para diferentes tipos de notificação
  info(message, options = {}) {
    return this.showNotification('info', message, options);
  }

  success(message, options = {}) {
    return this.showNotification('success', message, options);
  }

  warning(message, options = {}) {
    return this.showNotification('warning', message, options);
  }

  error(message, options = {}) {
    return this.showNotification('error', message, options);
  }

  // Método específico para notificações de senha
  senhaEmitida(numeroSenha, options = {}) {
    const message = `Nova senha emitida: ${numeroSenha}`;
    return this.showNotification('senha-emitida', message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // Método para notificações de transferência
  senhaTransferida(numeroSenha, remetente, motivo = '', options = {}) {
    const message = `📤 NOVA SENHA TRANSFERIDA: ${numeroSenha} de ${remetente}${motivo ? ` - ${motivo}` : ''}`;
    return this.showNotification('senha-transferida', message, {
      position: "top-right",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // Método para notificações de senha restaurada (uma vez por sessão REAL)
  senhaRestaurada(tipo = 'chamada', senhaId = null, options = {}) {
    let message;
    if (tipo === 'atendimento') {
      message = 'Atendimento em andamento restaurado do servidor!';
    } else {
      message = 'Senha chamada restaurada! Clique em "Iniciar" para começar o atendimento.';
    }
    
    // Para notificações de restauração, usar um controle mais rigoroso
    // Criar ID baseado no tipo + usuário + data de login
    const userId = this.getCurrentUserId();
    const loginTimestamp = this.getLoginTimestamp();
    const sessionId = `restauracao-${tipo}-${userId}-${loginTimestamp}`;
    
    // Verificar se já foi exibida nesta sessão REAL (desde o login)
    if (this.sessionNotifications.has(sessionId)) {
      console.log(`🚫 Notificação de restauração já exibida nesta sessão de login: ${message}`);
      return null;
    }
    
    // Registrar que foi exibida nesta sessão
    this.sessionNotifications.add(sessionId);
    
    // Salvar no localStorage para persistir entre recarregamentos
    this.saveSessionNotificationToStorage(sessionId);
    
    return this.showNotification('senha-restaurada', message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // Método auxiliar para obter ID do usuário atual
  getCurrentUserId() {
    try {
      const authData = localStorage.getItem('authToken') || localStorage.getItem('user');
      if (authData) {
        const userData = JSON.parse(authData);
        return userData.id || userData.userId || 'unknown';
      }
    } catch (error) {
      console.warn('Erro ao obter ID do usuário:', error);
    }
    return 'unknown';
  }

  // Método auxiliar para obter timestamp de login
  getLoginTimestamp() {
    try {
      // Tentar obter timestamp de login salvo
      let loginTime = localStorage.getItem('loginTimestamp');
      
      if (!loginTime) {
        // Se não existir, criar um novo (primeira vez na sessão)
        loginTime = Date.now().toString();
        localStorage.setItem('loginTimestamp', loginTime);
      }
      
      return loginTime;
    } catch (error) {
      console.warn('Erro ao obter timestamp de login:', error);
      return Date.now().toString();
    }
  }

  // Método para salvar notificações de sessão no localStorage
  saveSessionNotificationToStorage(sessionId) {
    try {
      const existingNotifications = JSON.parse(localStorage.getItem('sessionNotifications') || '[]');
      if (!existingNotifications.includes(sessionId)) {
        existingNotifications.push(sessionId);
        localStorage.setItem('sessionNotifications', JSON.stringify(existingNotifications));
      }
    } catch (error) {
      console.warn('Erro ao salvar notificação de sessão:', error);
    }
  }

  // Método para carregar notificações de sessão do localStorage
  loadSessionNotificationsFromStorage() {
    try {
      const savedNotifications = JSON.parse(localStorage.getItem('sessionNotifications') || '[]');
      savedNotifications.forEach(sessionId => {
        this.sessionNotifications.add(sessionId);
      });
      console.log(`📋 Carregadas ${savedNotifications.length} notificações de sessão do localStorage`);
    } catch (error) {
      console.warn('Erro ao carregar notificações de sessão:', error);
    }
  }

  // Método para limpar notificações de sessão (chamado no logout)
  clearSessionOnLogout() {
    this.sessionNotifications.clear();
    localStorage.removeItem('sessionNotifications');
    localStorage.removeItem('loginTimestamp');
    console.log('🧹 Notificações de sessão limpas no logout');
  }

  // ==========================================
  // MÉTODOS PARA ATENDIMENTO
  // ==========================================

  atendimentoIniciado(numeroSenha, options = {}) {
    const message = `Atendimento iniciado para senha ${numeroSenha}`;
    return this.showNotification('atendimento-iniciado', message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  atendimentoFinalizado(numeroSenha, options = {}) {
    const message = `Atendimento finalizado para senha ${numeroSenha}`;
    return this.showNotification('atendimento-finalizado', message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  senhaCancelada(numeroSenha, motivo = 'cancelada', options = {}) {
    const message = `Senha ${numeroSenha} ${motivo}`;
    return this.showNotification('senha-cancelada', message, {
      position: "top-right",
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // ==========================================
  // MÉTODOS PARA CONEXÃO
  // ==========================================

  conexaoEstabelecida(options = {}) {
    const message = 'Conectado ao servidor de senhas';
    return this.showNotification('conexao-estabelecida', message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  conexaoPerdida(options = {}) {
    const message = 'Desconectado do servidor de senhas. Tentando reconectar...';
    return this.showNotification('conexao-perdida', message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  erroConexao(erro, tentativa = 1, maxTentativas = 10, options = {}) {
    const message = `Erro de conexão: ${erro}. Tentativa ${tentativa}/${maxTentativas}`;
    return this.showNotification('erro-conexao', message, {
      position: "top-right",
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // ==========================================
  // MÉTODOS PARA CHAMADAS
  // ==========================================

  chamadaConcluida(numeroSenha, options = {}) {
    const message = `Senha ${numeroSenha} chamada com sucesso`;
    return this.showNotification('chamada-concluida', message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  chamadaEnfileirada(posicao, options = {}) {
    const message = `Sua senha foi enfileirada. Posição: ${posicao}`;
    return this.showNotification('chamada-enfileirada', message, {
      position: "top-right",
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // ==========================================
  // MÉTODOS PARA SISTEMA
  // ==========================================

  erroSistema(mensagem, options = {}) {
    const message = `Erro do sistema: ${mensagem}`;
    return this.showNotification('erro-sistema', message, {
      position: "top-right",
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    });
  }

  // ==========================================
  // MÉTODO ESPECIAL PARA ALTERAÇÃO DE GUICHÊ
  // ==========================================

  guicheAlterado(numeroGuiche, options = {}) {
    // Para alterações de guichê, sempre dismissar a notificação anterior
    // e mostrar a nova, mesmo que seja muito rápida
    const message = `Guichê alterado para ${numeroGuiche} com sucesso!`;
    const id = 'guiche-alterado'; // ID fixo para todas as notificações de guichê
    
    // Sempre remover a notificação anterior de guichê
    if (this.activeGuicheToast) {
      toast.dismiss(this.activeGuicheToast);
    }
    
    // Registrar e exibir nova notificação
    this.recordNotification(id);
    
    // Rastrear para debugging
    notificationDebugger.trackNotification('guiche-alterado', message, 'NotificationManager');
    
    // Criar nova notificação e armazenar sua referência
    this.activeGuicheToast = toast.success(message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: id, // ID único para permitir dismiss
      ...options
    });
    
    return this.activeGuicheToast;
  }

  // ==========================================
  // MÉTODO PARA CONFIGURAR RATE LIMITING GLOBAL
  // ==========================================

  configurarRateLimit(intervaloMinimo = 5000) {
    this.cleanupInterval = intervaloMinimo;
    console.log(`🔧 Rate limiting configurado para ${intervaloMinimo}ms`);
  }

  // Método para limpar todas as notificações
  clear() {
    this.recentNotifications.clear();
    this.sessionNotifications.clear();
    toast.dismiss();
  }

  // Método para limpar notificações de sessão (útil para testes)
  clearSession() {
    this.sessionNotifications.clear();
  }

  // ==========================================
  // MÉTODO PARA ESTATÍSTICAS DE USO
  // ==========================================

  getStats() {
    return {
      totalNotifications: this.recentNotifications.size,
      sessionNotifications: this.sessionNotifications.size,
      blockedNotifications: Array.from(this.recentNotifications.entries())
        .filter(([id, timestamp]) => Date.now() - timestamp < 5000).length
    };
  }
}

// Instância singleton
const notificationManager = new NotificationManager();

export default notificationManager; 